import type ImageType from "@travily/types/shared/Image";
import { Image } from "@ui/components/Image";
import type { ReactNode } from "react";

export interface ResolvedImageProps {
  /** Structured image (new path — uses `<Image>` with srcset, blurhash, lazy loading). */
  image?: ImageType | null;
  /** Legacy string URL. Prefer `image`. */
  src?: string;
  /** Alt text for the legacy `<img>`, or an `image.alt` override on the structured path. */
  alt?: string;
  className?: string;
  /** CSS `sizes` hint forwarded to `<Image>` (structured path only). */
  sizes?: string;
  /** LCP hint forwarded to `<Image>` (structured path only). */
  priority?: boolean;
  /** Rendered when neither `image` nor `src` is provided. */
  fallback?: ReactNode;
}

/**
 * Resolves the dual image surface shared by `CoverImage` and `CardImage`: a
 * structured `image` payload renders through `<Image>` (srcset, blurhash,
 * lazy loading), a legacy `src` string renders a bare `<img>`, and neither
 * renders the `fallback` (default: nothing).
 */
export function ResolvedImage({ image, src, alt, className, sizes, priority, fallback = null }: ResolvedImageProps) {
  if (image) {
    return (
      <Image image={image} alt={alt} sizes={sizes} priority={priority} className={className} fallback={fallback} />
    );
  }

  if (src) {
    return <img alt={alt ?? ""} className={className} src={src} />;
  }

  return <>{fallback}</>;
}
